import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logofull from "assets/logo.webp";
import { MEET_PAGE_ENABLED } from "config/featureFlags";
import "./navbar.css";
import "styles/siteChrome.css";

const REGISTER_URL = `https://app.jackrabbitclass.com/regv2.asp?id=${process.env.REACT_APP_ORGID}`;
const PORTAL_URL =
  "https://app.jackrabbitclass.com/jr3.0/ParentPortal/Login?orgID=539177";

function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  const programTitles = useSelector(
    (state) => state.contentfulReducer.programTitles
  );
  const calendar = useSelector((state) => state.contentfulReducer.calendar);
  const calendarLabel = calendar?.label || "Calendar";

  const closeAll = useCallback(() => {
    setMobileOpen(false);
    setProgramsOpen(false);
    setEventsOpen(false);
  }, []);

  const navigate = () => {
    closeAll();
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    closeAll();
  }, [location.pathname, closeAll]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setProgramsOpen(false);
        setEventsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeAll]);

  const togglePrograms = () => {
    setProgramsOpen((v) => !v);
    setEventsOpen(false);
  };

  const toggleEvents = () => {
    setEventsOpen((v) => !v);
    setProgramsOpen(false);
  };

  return (
    <header className="site-header" ref={navRef}>
      <div className="site-header__inner">
        <Link to="/" className="site-header__logo" onClick={navigate}>
          <img
            src={logofull}
            alt="Ace Gymnastics home"
            width="120"
            height="120"
          />
        </Link>

        <button
          type="button"
          className={`site-header__toggle ${mobileOpen ? "is-open" : ""}`}
          aria-expanded={mobileOpen}
          aria-controls="site-nav"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav
          id="site-nav"
          className={`site-header__nav ${mobileOpen ? "is-open" : ""}`}
          aria-label="Main navigation"
        >
          <ul className="site-header__links">
            <li>
              <Link to="/" onClick={navigate}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/contact" onClick={navigate}>
                Contact
              </Link>
            </li>
            <li>
              <Link to="/staff" onClick={navigate}>
                Staff
              </Link>
            </li>
            <li
              className={`site-header__dropdown ${
                programsOpen ? "is-open" : ""
              }`}
            >
              <button
                type="button"
                className="site-header__dropdown-trigger"
                aria-expanded={programsOpen}
                aria-haspopup="true"
                onClick={togglePrograms}
              >
                Programs
                <span className="site-header__chevron" aria-hidden="true" />
              </button>
              <ul className="site-header__dropdown-menu">
                {programTitles.map((program) => (
                  <li key={program.fields.displayName}>
                    <Link
                      to={`/programs/program-details/${program.fields.displayName}`}
                      onClick={navigate}
                    >
                      {program.fields.displayName}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li
              className={`site-header__dropdown ${eventsOpen ? "is-open" : ""}`}
            >
              <button
                type="button"
                className="site-header__dropdown-trigger"
                aria-expanded={eventsOpen}
                aria-haspopup="true"
                onClick={toggleEvents}
              >
                Events
                <span className="site-header__chevron" aria-hidden="true" />
              </button>
              <ul className="site-header__dropdown-menu">
                <li>
                  <Link to="/events" onClick={navigate}>
                    All Events
                  </Link>
                </li>
                {MEET_PAGE_ENABLED && (
                  <li>
                    <Link
                      to="/bourbon-city-spectacular"
                      onClick={navigate}
                      data-track="meet_section_nav_click"
                      data-track-location="site_nav"
                      data-track-label="Bourbon City Spectacular"
                    >
                      Bourbon City Spectacular
                    </Link>
                  </li>
                )}
              </ul>
            </li>
            <li>
              <Link
                to="/calendar"
                onClick={navigate}
                data-track="calendar_click"
                data-track-location="nav"
              >
                {calendarLabel}
              </Link>
            </li>
            <li>
              <Link to="/employment" onClick={navigate}>
                Employment
              </Link>
            </li>
          </ul>

          <div className="site-header__actions">
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="site-btn site-btn--ghost site-header__portal"
              onClick={closeAll}
              data-track="parent_portal_click"
              data-track-location="nav"
            >
              Parent Portal
            </a>
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="site-btn site-btn--accent"
              onClick={closeAll}
              data-track="register_click"
              data-track-location="nav"
            >
              Register
            </a>
          </div>
        </nav>
      </div>

      {mobileOpen && (
        <button
          type="button"
          className="site-header__backdrop"
          aria-label="Close menu"
          onClick={closeAll}
        />
      )}
    </header>
  );
}

export default NavBar;
